import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Tag, Plus, Clock } from "lucide-react";

// Midnight countdown
const useMidnightCountdown = () => {
    const [remaining, setRemaining] = useState("");
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            const midnight = new Date(now);
            midnight.setHours(24, 0, 0, 0);
            const diff = midnight - now;
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setRemaining(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, []);
    return remaining;
};

export const Offers = () => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addBundle } = useCart();
    const navigate = useNavigate();
    const countdown = useMidnightCountdown();

    useEffect(() => {
        api.get("/offers").then(({ data }) => { setOffers(data); setLoading(false); });
    }, []);

    const grab = (offer) => {
        if (!offer.bundled_items?.length) {
            toast.error("Η προσφορά δεν είναι διαθέσιμη");
            return;
        }
        addBundle(offer.bundled_items, offer.coupon_code);
        toast.success(`Η προσφορά "${offer.title}" προστέθηκε.`);
    };

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-12" data-testid="offers-page">
            <header className="mb-10">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1.5 bg-brand-red text-white px-3 py-1.5 text-[11px] font-heading uppercase tracking-widest font-black animate-pulse">
                        <Flame size={12} /> Limited Offer
                    </span>
                    <span className="inline-flex items-center gap-1.5 border border-brand-yellow/40 bg-brand-yellow/5 text-brand-yellow px-3 py-1.5 text-[11px] font-heading uppercase tracking-widest">
                        <Clock size={12} /> Λήγει σε <span className="font-mono font-black ml-1" data-testid="offers-countdown">{countdown}</span>
                    </span>
                </div>
                <h1 className="headline text-5xl sm:text-6xl">Φάε δύο, πλήρωσε ένα.</h1>
                <p className="text-white/60 mt-3 max-w-2xl text-sm leading-relaxed">
                    Επιλεγμένες προσφορές 1+1 και combos με μεγάλες εκπτώσεις που ανανεώνονται καθημερινά.
                </p>
            </header>

            {loading ? (
                <p className="text-white/50">Φόρτωση...</p>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {offers.map((o, idx) => (
                        <article
                            key={o.id}
                            data-testid={`offer-card-${o.id}`}
                            className="group bg-[#16161a] border border-white/10 rounded-sm overflow-hidden shadow-xl shadow-black/30 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-red hover:shadow-brand-red/10 animate-fade-up flex flex-col"
                            style={{ animationDelay: `${idx * 80}ms` }}
                        >
                            <div className="relative aspect-[5/3] overflow-hidden bg-black">
                                <img
                                    src={o.image_url}
                                    alt={o.title}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                                <span className="absolute top-3 left-3 bg-brand-red text-white text-[11px] font-heading uppercase tracking-widest px-3 py-1 font-black shadow-lg">
                                    {o.badge}
                                </span>
                                <span className="absolute top-3 right-3 bg-brand-yellow text-black text-[10px] font-heading uppercase tracking-widest px-2 py-1 font-black">
                                    {o.subtitle}
                                </span>
                            </div>

                            <div className="p-5 flex flex-col flex-1">
                                <h3 className="font-heading uppercase text-xl leading-tight">{o.title}</h3>
                                <p className="text-sm text-white/60 mt-2 leading-relaxed line-clamp-3 flex-1">{o.description}</p>

                                <div className="mt-4 flex items-end gap-3">
                                    <span className="text-3xl font-heading font-black text-brand-yellow">
                                        €{o.price_from.toFixed(2)}
                                    </span>
                                    <span className="text-sm text-white/40 line-through pb-1">
                                        €{o.original_price.toFixed(2)}
                                    </span>
                                </div>

                                <div className="mt-3 inline-flex items-center gap-1.5 self-start text-[11px] uppercase tracking-widest text-brand-yellow border border-brand-yellow/40 bg-brand-yellow/5 px-2 py-1">
                                    <Tag size={11} /> Κουπόνι: {o.coupon_code}
                                </div>

                                <button
                                    data-testid={`offer-grab-${o.id}`}
                                    onClick={() => grab(o)}
                                    className="mt-5 w-full h-11 flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redhover text-white font-heading uppercase tracking-wider text-sm font-black rounded-sm transition-all hover:-translate-y-0.5 active:scale-95 shadow-lg shadow-brand-red/20"
                                >
                                    <Plus size={16} strokeWidth={3} /> Πάρε την Προσφορά
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <div className="mt-12 p-6 border border-white/10 bg-[#16161a]/60 text-sm text-white/70 leading-relaxed">
                <strong className="text-white">Πώς δουλεύει:</strong> πατώντας <em>"Πάρε την προσφορά"</em>,
                τα προϊόντα προστίθενται στο καλάθι και ο κωδικός κουπονιού αποθηκεύεται. Στη σελίδα του checkout
                εφαρμόζεται αυτόματα η έκπτωση.
                <button
                    onClick={() => navigate("/menu")}
                    className="ml-2 text-brand-red hover:underline"
                    data-testid="offers-go-menu"
                >
                    Δες το πλήρες μενού →
                </button>
            </div>
        </div>
    );
};
